import React from "react";
import { Helmet } from "react-helmet";
import speechBubbleBanner from "../assets/Red Wine_Logo (1).svg";
import matthewHeadshotPng from "../assets/Matthew_headshot.png";
import charetteHeadshotPng from "../assets/Charette_headshot.png";
import Footer from "./Footer";
import Nav from "./Nav";
import { SEO_SHARE_IMAGES, toAbsoluteAssetUrl } from "../lib/seoImages";
import "../styles/homepage-redesign.css";
import "../styles/about.css";

function About() {
  const pageUrl = "https://speakyourmenu.com/about";
  const shareImage = SEO_SHARE_IMAGES.about;

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>About Speak Your Menu & Founders | Restaurant Menu Training Experts</title>
        <meta
          name="description"
          content="Meet the founders of Speak Your Menu and learn how our menu training app helps restaurants boost sales, speed onboarding, and improve guest satisfaction. Discover our mission and the story behind our approach."
        />
        {/* Open Graph */}
      <meta property="og:title" content="About Speak Your Menu & Founders" />
      <meta property="og:description" content="Meet the founders and see how Speak Your Menu transforms restaurant training with interactive, real-world learning." />
        <meta property="og:type" content="website" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={shareImage} />
        <meta property="og:site_name" content="Speak Your Menu" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="About Speak Your Menu & Founders" />
      <meta name="twitter:description" content="Meet the founders and see how Speak Your Menu transforms restaurant training with interactive, real-world learning." />
      <meta name="twitter:image" content={shareImage} />
        <meta name="twitter:site" content="@speakyourmenu" />
        {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />
        {/* BreadcrumbList JSON-LD */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://speakyourmenu.com/"},
            {"@type": "ListItem", "position": 2, "name": "About", "item": "https://speakyourmenu.com/about"}
          ]
        })}</script>
        {/* Organization JSON-LD */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Speak Your Menu",
          "url": "https://speakyourmenu.com",
          "logo": "https://speakyourmenu.com/logo192.png",
          "sameAs": [
            "https://www.instagram.com/speakyourmenu",
            "https://www.facebook.com/speakyourmenu",
            "https://www.linkedin.com/company/speakyourmenu"
          ]
        })}</script>
        {/* AboutPage JSON-LD */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Speak Your Menu & Founders",
          "url": pageUrl,
          "description": "Learn about Speak Your Menu, our mission, and meet the founders.",
          "isPartOf": { "@type": "WebSite", "name": "Speak Your Menu", "url": "https://speakyourmenu.com" },
          "primaryImageOfPage": { "@type": "ImageObject", "url": shareImage }
        })}</script>
        {/* Person JSON-LD for founders */}
        <script type="application/ld+json">{JSON.stringify([
          {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Matthew Denune",
            "jobTitle": "CEO & Co-Founder",
            "image": toAbsoluteAssetUrl(matthewHeadshotPng),
            "worksFor": { "@type": "Organization", "name": "Speak Your Menu" },
            "description": "Hospitality innovator with 15+ years across fine dining, beverage distribution, and FOH training; WSET Diploma and certified in sake service. Leads Speak Your Menu to modernize staff training and product knowledge.",
            "url": pageUrl
          },
          {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Charette Denune",
            "jobTitle": "COO & Co-Founder",
            "image": toAbsoluteAssetUrl(charetteHeadshotPng),
            "worksFor": { "@type": "Organization", "name": "Speak Your Menu" },
            "description": "Operations leader focused on human-centered design and learning. Drives product, culture, and workflows to build confidence and performance for restaurant teams.",
            "url": pageUrl
          }
        ])}</script>
        {/* To update keywords or URLs, edit above meta tags and JSON-LD. */}
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="about-page homepage-redesign">
        <section className="homepage-section homepage-hero-section about-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell about-hero-shell">
            <div className="about-hero-grid">
              <div className="about-hero-copy">
                <h1 className="homepage-hero-title about-hero-title">Who We Are</h1>
                <p className="homepage-hero-subhead about-hero-body">
                  At{" "}
                  <strong style={{ color: "var(--color-wine-red)" }}>
                    Speak Your Menu
                  </strong>
                  , we’re passionate about hospitality and dedicated to
                  transforming restaurant education. With years of industry
                  experience, we've trained thousands of restaurant professionals
                  and uncovered a powerful insight—menu knowledge is like
                  learning a new language. By applying proven language-learning
                  techniques to staff training, we help restaurants enhance
                  service, boost sales, and improve employee retention. Our goal
                  is simple: empower teams with the confidence and knowledge to
                  deliver exceptional dining experiences.
                </p>
              </div>

              <div className="about-hero-mark" aria-hidden="true">
                <div className="about-hero-mark-frame">
                  <img
                    src={speechBubbleBanner}
                    alt=""
                    className="about-hero-mark-image"
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt about-principles-section">
          <div className="homepage-shell">
            <div className="about-principles-grid">
              <article className="homepage-surface-card about-info-card">
                <h2 className="homepage-section-title about-card-title">Our Philosophy</h2>
                <p className="homepage-card-copy about-card-body">
                  Your menu is more than a list of offerings—it’s the story of
                  your restaurant. When your staff can speak this story fluently,
                  they provide a dining experience that keeps customers coming
                  back.
                </p>
              </article>

              <article className="homepage-surface-card about-info-card">
                <h2 className="homepage-section-title about-card-title">Our Mission</h2>
                <p className="homepage-card-copy about-card-body">
                  To revolutionize restaurant training by delivering an
                  accessible, affordable, and effective platform that increases
                  profits, boosts customer satisfaction, and empowers teams.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section id="founders" className="homepage-section about-founders-section">
          <div className="homepage-shell">
            <div className="about-founders-header">
              <h2 className="homepage-section-title about-founders-title">Meet the Founders</h2>
            </div>

            <div className="about-founders-grid">
              <article className="homepage-surface-card about-founder-card">
                <div className="founder-photo-wrapper">
                  <img
                    src={require("../assets/Matthew_headshot.jpeg")}
                    alt="Matthew Denune headshot"
                    className="founder-photo founder-photo--matthew"
                    loading="lazy"
                  />
                </div>
                <div className="about-founder-body">
                  <h3 className="about-founder-name">Matthew Denune</h3>
                  <p className="about-founder-role">CEO &amp; Co-Founder</p>
                  <p className="about-founder-bio">
                    Matthew Denune is a hospitality innovator with over 15 years of hands-on experience spanning fine dining, beverage distribution, and front-of-house training. A holder of the prestigious WSET Diploma—earned by fewer than 10,000 professionals worldwide—and certified in sake service, Matthew blends deep product knowledge with operational expertise.
                    <br /><br />
                    Throughout his career, Matthew witnessed a common challenge: restaurants lacked an efficient, engaging way to train staff on their menus. Determined to solve this, he co-founded Speak Your Menu to bring interactive, gamified learning to the restaurant floor. Today, he leads the company with a clear mission—to empower hospitality teams through better training, sharper product knowledge, and modern tech designed for the industry's real-world pace.
                  </p>
                </div>
              </article>

              <article className="homepage-surface-card about-founder-card">
                <div className="founder-photo-wrapper">
                  <img
                    src={require("../assets/Charette_headshot.jpeg")}
                    alt="Charette Denune headshot"
                    className="founder-photo founder-photo--charette"
                    loading="lazy"
                  />
                </div>
                <div className="about-founder-body">
                  <h3 className="about-founder-name">Charette Denune</h3>
                  <p className="about-founder-role">COO &amp; Co-Founder</p>
                  <p className="about-founder-bio">
                    Charette Denune blends operational excellence with human-centered design, drawing on years of hands-on experience in the restaurant industry and a deep understanding of how people learn, connect, and grow in the workplace. She holds a degree in Organizational Behavior and Communications, where she focused on the intersection of team dynamics, leadership, and spirituality in professional environments.
                    <br /><br />
                    Before co-founding Speak Your Menu, Charette worked in restaurants across multiple roles, gaining firsthand insight into the challenges of staff training, turnover, and inconsistent guest experiences. That experience—paired with her academic foundation—fueled her vision for a platform that doesn’t just teach menus, but empowers teams.
                    <br /><br />
                    At Speak Your Menu, Charette leads operations, product development, and internal culture. She’s the architect behind the company’s most intuitive workflows, and a driving force in creating a learning environment rooted in clarity, empathy, and purpose. Her work ensures that the platform supports not just performance, but also the growth and confidence of every team member who uses it.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default About;
